import React, { useEffect, useMemo, useState } from "react";

const TAB_ORDER = [
  { key: "transcript", labelKey: "livebridge_tab_transcript", fallback: "Transcript" },
  { key: "cleaned", labelKey: "livebridge_tab_cleaned", fallback: "Cleaned" },
  { key: "structured", labelKey: "livebridge_tab_structured", fallback: "Structured" },
  { key: "translation", labelKey: "livebridge_tab_translation", fallback: "Translation" }
];

function getTabText(result, activeTab, emptyTranslationText) {
  if (!result) {
    return "";
  }

  if (activeTab === "transcript") {
    return result.rawTranscript || "";
  }

  if (activeTab === "cleaned") {
    return result.cleanText || "";
  }

  if (activeTab === "structured") {
    return result.structuredNotes || "";
  }

  if (activeTab === "translation") {
    return (
      result.translatedText ||
      emptyTranslationText
    );
  }

  return "";
}

export { getTabText };

function ResultTabs({ result, onRetry, t }) {
  const [tab, setTab] = useState("cleaned");
  const visibleTabs = useMemo(() => {
    return TAB_ORDER.filter((item) => item.key !== "translation" || result?.targetLanguage);
  }, [result]);
  const emptyTranslationText = t
    ? t("livebridge_translation_placeholder")
    : "Translation could not be generated right now. Try again in a moment.";

  useEffect(() => {
    if (!visibleTabs.some((item) => item.key === tab)) {
      setTab("cleaned");
    }
  }, [tab, visibleTabs]);

  const content = useMemo(() => {
    const currentText = getTabText(result, tab, emptyTranslationText);
    if (currentText) {
      return currentText;
    }

    return "";
  }, [emptyTranslationText, result, tab]);

  const copyCurrent = async () => {
    if (!content || !navigator?.clipboard?.writeText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(content);
    } catch (_error) {
      // Keep the MVP calm: silently fail if clipboard is unavailable.
    }
  };

  const downloadTxt = () => {
    if (!content || typeof window === "undefined") {
      return;
    }

    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `livebridge-${tab}.txt`;
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="liveBridgeResultSurface">
      <div className="liveBridgeResultTabs" role="tablist" aria-label={t("livebridge_result_tabs")}>
        {visibleTabs.map((item) => {
          const isActive = tab === item.key;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`liveBridgeResultTab${isActive ? " active" : ""}`}
              onClick={() => setTab(item.key)}
            >
              {t ? t(item.labelKey) : item.fallback}
            </button>
          );
        })}
      </div>

      <div className="liveBridgeResultBody" role="tabpanel">
        {content ? (
          <pre className="liveBridgeResultText">{content}</pre>
        ) : (
          <div className="liveBridgeViewerEmpty">
            {t ? t("livebridge_organize_empty") : "Nothing yet."}
          </div>
        )}
      </div>

      <div className="liveBridgeResultActions">
        <button
          className="homeSecondaryButton liveBridgeButtonUtility"
          type="button"
          onClick={copyCurrent}
          disabled={!content}
        >
          {t ? t("livebridge_action_copy") : "Copy"}
        </button>

        <button
          className="homeSecondaryButton liveBridgeButtonUtility"
          type="button"
          onClick={downloadTxt}
          disabled={!content}
        >
          {t ? t("livebridge_action_download") : "Download TXT"}
        </button>

        <button
          className="homeSecondaryButton liveBridgeButtonRetry"
          type="button"
          onClick={onRetry}
          disabled={!result?.rawTranscript}
        >
          {t ? t("livebridge_action_retry") : "Retry"}
        </button>

      </div>

      <div className="liveBridgeMetaGrid liveBridgeMetaGridTop">
        <div className="liveBridgeMetaCard">
          <div className="liveBridgeMetaLabel">
            {t ? t("livebridge_result_title") : "Title"}
          </div>
          <div className="liveBridgeSummaryText">
            {result?.title || (t ? t("livebridge_organize_empty") : "Nothing yet.")}
          </div>
        </div>

        <div className="liveBridgeMetaCard">
          <div className="liveBridgeMetaLabel">
            {t ? t("livebridge_result_summary") : "Summary"}
          </div>
          <div className="liveBridgeSummaryText">
            {result?.summary || (t ? t("livebridge_organize_empty") : "Nothing yet.")}
          </div>
        </div>
      </div>

      <div className="liveBridgeMetaGrid">
        <div className="liveBridgeMetaCard">
          <div className="liveBridgeMetaLabel">
            {t ? t("livebridge_key_points") : "Key Points"}
          </div>
          <ul className="liveBridgeMetaList">
            {(result?.keyPoints || []).length ? (
              result.keyPoints.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)
            ) : (
              <li>{t ? t("livebridge_organize_empty") : "Nothing yet."}</li>
            )}
          </ul>
        </div>

        <div className="liveBridgeMetaCard">
          <div className="liveBridgeMetaLabel">
            {t ? t("livebridge_action_items") : "Action Items"}
          </div>
          <ul className="liveBridgeMetaList">
            {(result?.actionItems || []).length ? (
              result.actionItems.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)
            ) : (
              <li>No clear action items.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

export { ResultTabs };

export default ResultTabs;
